/*
 * File: OnlineUsersWidget.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 2, 2025 at 11:35 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

// Services
import { getOnlineMembers } from '../../services/user';

// Store
import { updateOnlineMembers } from '../../store/slices/metric/metric.slice';

// Components
import { Typography } from '../Typography';

// Styles
import * as S from './OnlineUsersWidget.styles';


/**
 * Component
 */

export const OnlineUsersWidget = ({
	className, actionText
}) => {

	// Get platform metrics from hook
	const onlineUsers = useSelector((state) => state.metric.onlineMembers);

	// Create references for components
	const pollingTimer = useRef();
	const isMounted = useRef(true);

	// Get hooks
	const dispatch = useDispatch();

	// Handle get online members
	const pollOnlineMembers = async () => {
		try {

			// Query for online numbers
			const { data: { result } } = await getOnlineMembers();

			// Update online members
			dispatch(updateOnlineMembers(result.online_members));

		} catch (_) {}
	};

	// Handle actions on online user count update
	useEffect(() => {
		if (onlineUsers === -1) {

			// Poll online members
			pollOnlineMembers();

		}
	}, [onlineUsers]);

	// Handle actions on component load
	useEffect(() => {

		// Initialize temporary state
		isMounted.current = true;

		// Start polling for online users
		pollingTimer.current = setInterval(() => {

			// Poll online members
			pollOnlineMembers();

		}, 60 * 1000);

		// Handle actions on dismount
		return () => {

			// Update mounted status
			isMounted.current = false;

			// Clear polling interval
			if (pollingTimer.current) {
				clearInterval(pollingTimer.current);
				pollingTimer.current = null;
			}
		};

	}, []);

	// Render component
	return (
		onlineUsers > 0 ? (
			<S.Wrapper className={className}>
				<Typography tag="p" weight="semibold" className={onlineUsers === -1 && 'shimmer shimmer-element'}>{onlineUsers}</Typography>
				<S.OnlineStatus>
					<S.OnlineIndicator />
					<Typography tag="p" variation="2">{actionText}</Typography>
				</S.OnlineStatus>
			</S.Wrapper>
		) : null
	);
};


/**
 * Configuration
 */

OnlineUsersWidget.displayName = 'OnlineUsersWidget';
OnlineUsersWidget.propTypes = {
	className: PropTypes.string,
	actionText: PropTypes.string
};
OnlineUsersWidget.defaultProps = {
	className: null,
	actionText: null
};
